<template>
  <div class="flickity__holder">
    <div ref="root" class="flickity">
      <slot />
    </div>
    <div class="flickity-controls">
      <div class="prev-nex-buttons">
        <button class="button button--previous" @click="goToPrevious" aria-label="Previous Slide">
          <arrow-right/>
        </button>
        <button class="button button--next" @click="goToNext" aria-label="Next Slide">
          <arrow-right/>
        </button>
      </div>

      <div class="progression__bar">
        <div class="progression__bar__completed" ref="progressBar"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'
import Flickity from 'flickity'

import ArrowRight from "./svg/ArrowRight.vue"
import ArrowLeft from "./svg/ArrowLeft.vue"

export default defineComponent({
  props: {
    options: Object,
  },
  components: {
    ArrowLeft,
    ArrowRight,
  },
  setup(props, {emit}) {
    let flickity = Flickity
    const root = ref<HTMLElement | null>(null)
    const progressBar = ref<HTMLElement | null>(null)

    onMounted(() => {
      flickity = new Flickity(root.value as HTMLElement, props.options)
      updateProgress()
      flickity.on('select', updateProgress)
      flickity.on('select', emitChangeEvent)
      flickity.on('select', equalizeHeights)

      // Initial emission of visible slides and height equalization
      setTimeout(() => {
        emitChangeEvent();
        equalizeHeights();
      }, 100);

      flickity.on('staticClick', function(event: Event, pointer: any, cellElement: HTMLElement, cellIndex: number) {
        emit('flickityStaticClick', cellIndex);
      });
    })

    onUnmounted(() => {
      flickity?.off('select', updateProgress)
      flickity?.off('select', emitChangeEvent)
      flickity?.destroy()
    })

    const goToPrevious = () => {
      if (flickity) {
        flickity!.previous()
      }
    }

    const goToNext = () => {
      if (flickity) {
        flickity!.next()
      }
    }

    const updateProgress = () => {
      if (flickity && progressBar.value) {
        const selectedIndex = flickity.selectedIndex
        const slideCount = flickity.slides.length
        const progressPercentage = ((selectedIndex + 1) / slideCount) * 100
        progressBar.value.style.width = `${progressPercentage}%`
      }
    }

    const emitChangeEvent = () => {
      if (flickity) {
        // Get visible slides based on Flickity's API
        const visibleSlides: number[] = [];
        const selectedIndex = flickity.selectedIndex;
        const slideCount = flickity.slides.length;
        
        // For groupCells enabled, we need to be more generous with visible slides
        if (props.options && props.options.groupCells) {
          // Get the carousel container width and slide width to calculate how many slides are actually visible
          const container = flickity.element;
          const containerWidth = container.clientWidth;
          
          // Get slide widths - in groupCells mode, all slides should be visible
          const slides = flickity.slides;
          let totalSlidesWidth = 0;
          
          // Calculate if all slides fit in the container
          slides.forEach((slide: any) => {
            totalSlidesWidth += slide.outerWidth;
          });
          
          // If all slides fit in the container, mark all as visible
          if (totalSlidesWidth <= containerWidth) {
            for (let i = 0; i < slideCount; i++) {
              visibleSlides.push(i);
            }
          } else {
            // Otherwise, be more generous and include more slides around the selected index
            const range = Math.ceil(slideCount / 2); // More generous range
            for (let i = Math.max(0, selectedIndex - range); i <= Math.min(slideCount - 1, selectedIndex + range); i++) {
              visibleSlides.push(i);
            }
          }
        } else {
          // For non-grouped cells, just add the selected slide
          visibleSlides.push(selectedIndex);
        }
        
        // Emit event with visible slides
        emit('change', visibleSlides);
      }
    }

    const equalizeHeights = () => {
      if (flickity && window.innerWidth < 768) { // Only on mobile
        const clientBlocks = flickity.element.querySelectorAll('.client__block');
        if (clientBlocks.length === 0) return;
        
        // Reset heights
        clientBlocks.forEach((block: Element) => {
          (block as HTMLElement).style.height = 'auto';
        });
        
        // Find the tallest block
        let maxHeight = 0;
        clientBlocks.forEach((block: Element) => {
          const height = (block as HTMLElement).offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Set all blocks to the max height
        clientBlocks.forEach((block: Element) => {
          (block as HTMLElement).style.height = `${maxHeight}px`;
        });
      }
    }

    return {
      root,
      goToPrevious,
      goToNext,
      progressBar,
    }
  },
})
</script>

<style scoped>
@import '~flickity/dist/flickity.css';
</style>
